import { useState, useEffect } from 'react'
import ConfirmationDialog from '../../../components/ReusableComponents/ConfirmationDialog/index'
import Dropdown from '../../../components/ReusableComponents/ManageHotel/Dropdown'
import LoadingIndicator from '../../../components/ReusableComponents/LoadingIndicator'
import userApi from '../../../services/api/user'
import styles from './EditCSMDialog.module.scss'

const EditCSMDialog = ({ currentCsmName, subsidiaryId, onClose, onConfirm }) => {
  const [csmUsers, setCsmUsers] = useState([])
  const [selectedCsmId, setSelectedCsmId] = useState('unassigned')
  const [isLoading, setIsLoading] = useState(true)
  const [note, setNote] = useState('')
  const [isLoadingNote, setIsLoadingNote] = useState(true)

  useEffect(() => {
    userApi
      .getCustomerSuccessManagers()
      .then(data => {
        setCsmUsers(data)
        // Find current CSM by matching name
        const currentCsm = data.find(csm => csm.fullName === currentCsmName)
        if (currentCsm) {
          setSelectedCsmId(currentCsm.id.toString())
        } else {
          setSelectedCsmId('unassigned')
        }
      })
      .catch(err => {
        console.error('Failed to fetch CSM users:', err)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [currentCsmName])

  useEffect(() => {
    if (subsidiaryId) {
      setIsLoadingNote(true)
      
      // Ensure subsidiaryId is properly formatted
      const subsidiaryIdStr = String(subsidiaryId).trim().replace(/[{}]/g, '')
      const noteKey = `csm-edit-${subsidiaryIdStr}`
      
      userApi
        .retrieveNote({
          key: noteKey,
          kind: 'csm-assignment',
          subsidiaryId: subsidiaryIdStr // Pass explicitly to avoid extraction issues
        })
        .then(response => {
          if (response && response.data) {
            setNote(response.data)
          }
        })
        .catch(err => {
          console.error('Failed to load note:', err)
          console.error('Error details - subsidiaryId:', subsidiaryId, 'key:', noteKey)
        })
        .finally(() => {
          setIsLoadingNote(false)
        })
    } else {
      console.warn('EditCSMDialog - subsidiaryId is missing or undefined')
    }
  }, [subsidiaryId])

  const handleConfirm = async () => {
    // Validate that note is provided
    if (!note.trim()) {
      return // Don't proceed if note is empty
    }

    // Save note (mandatory)
    if (!subsidiaryId) {
      console.error('Cannot save note: subsidiaryId is missing')
      return
    }
    
    try {
      // Convert to string and ensure it's a valid Guid format
      let subsidiaryIdStr = typeof subsidiaryId === 'string' 
        ? subsidiaryId.trim()
        : String(subsidiaryId).trim()
      
      // Remove any braces if present (Guid format can include {})
      subsidiaryIdStr = subsidiaryIdStr.replace(/[{}]/g, '')
      
      // Validate it looks like a Guid (basic check)
      if (!subsidiaryIdStr || subsidiaryIdStr.length < 32) {
        console.error('Invalid subsidiaryId format:', subsidiaryIdStr)
        return
      }
      
      const noteKey = `csm-edit-${subsidiaryIdStr}`
      
      await userApi.addOrUpdateNote({
        key: noteKey,
        kind: 'csm-assignment',
        data: note.trim(),
        subsidiaryId: subsidiaryIdStr // Pass explicitly to avoid extraction issues
      })
    } catch (err) {
      console.error('Failed to save note:', err)
      console.error('Error details - subsidiaryId:', subsidiaryId)
      // Don't proceed if note save fails since it's mandatory
      return
    }
    
    // Call the original onConfirm with the selected CSM ID
    onConfirm(selectedCsmId === 'unassigned' ? null : selectedCsmId)
  }

  const isButtonDisabled = !note.trim() || isLoading || isLoadingNote

  const options = {
    unassigned: 'Unassigned',
    ...csmUsers.reduce((acc, csm) => {
      acc[csm.id.toString()] = csm.fullName
      return acc
    }, {})
  }

  return (
    <ConfirmationDialog
      title="Edit CSM"
      theme="blacklist"
      positiveMessage="Save changes"
      onConfirm={handleConfirm}
      onClose={onClose}
      isButtonDisabled={isButtonDisabled}
    >
      <div className={styles.container}>
        {isLoading ? (
          <LoadingIndicator />
        ) : (
          <>
            <Dropdown
              label="Customer Success Manager"
              defaultVal="Select CSM"
              options={options}
              value={selectedCsmId}
              onChange={e => setSelectedCsmId(e.target.value)}
            />
            <div className={styles.noteSection}>
              <label className={styles.noteLabel}>
                Notes/Comments <span className={styles.required}>*</span>
              </label>
              <textarea
                className={styles.noteTextarea}
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="Add notes about this CSM assignment (required)..."
                rows={4}
                disabled={isLoadingNote}
                required
              />
              {isLoadingNote && (
                <div className={styles.noteLoading}>Loading note...</div>
              )}
              {!note.trim() && !isLoadingNote && (
                <div className={styles.noteError}>Notes/Comments are required</div>
              )}
            </div>
          </>
        )}
      </div>
    </ConfirmationDialog>
  )
}

export default EditCSMDialog


